import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, ChevronDown, Check, GraduationCap, Briefcase, HeartHandshake, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

const ICON = { school: GraduationCap, care: HeartHandshake, business: Briefcase };

export function LocationSwitcher() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [locations, setLocations] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/corporate/locations").then(({ data }) => setLocations(data.filter((l) => !l.archived))).catch(() => {}); }, []);
  const current = locations.find((l) => l.id === user?.company_id);
  const list = useMemo(() => locations.filter((l) => l.name.toLowerCase().includes(q.trim().toLowerCase())), [locations, q]);

  const switchTo = async (l) => {
    if (busy) return;
    setBusy(l.id);
    try {
      const { data } = await api.post("/corporate/switch", { company_id: l.id });
      if (data.token) localStorage.setItem("vlog_token", data.token);
      await refresh?.();
      toast.success(`Switched to ${l.name}`);
      nav("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Switch failed");
    } finally {
      setBusy("");
    }
  };

  if (locations.length === 0) return null;
  return (
    <DropdownMenu onOpenChange={(o) => { if (!o) setQ(""); }}>
      <DropdownMenuTrigger asChild>
        <button data-testid="location-switcher-trigger"
          className="inline-flex h-[52px] items-center gap-2 rounded-full bg-white/80 backdrop-blur border border-slate-200 pl-3 pr-3 text-left shadow-sm hover:bg-white hover:shadow transition outline-none focus-visible:ring-4 focus-visible:ring-primary/20">
          <MapPin className="h-4 w-4 text-slate-500" />
          <div className="hidden sm:block min-w-0 max-w-[200px]">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 leading-tight">My location</div>
            <div className="font-display text-sm font-bold leading-tight text-slate-900 truncate" data-testid="location-switcher-current">{current?.name || "Choose a location"}</div>
          </div>
          <ChevronDown className="h-4 w-4 text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-80 rounded-xl p-1.5 shadow-xl" data-testid="location-switcher-menu">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-widest text-slate-500">Open a location's admin console</DropdownMenuLabel>
        {locations.length > 6 && (
          <div className="px-1.5 pb-1.5" onKeyDown={(e) => e.stopPropagation()}>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search locations…" data-testid="location-switcher-search"
                className="w-full h-9 rounded-lg border border-slate-200 bg-slate-50 pl-8 pr-2 text-sm outline-none focus:border-primary focus:bg-white" />
            </div>
          </div>
        )}
        <div className="max-h-72 overflow-y-auto">
          {list.map((l) => {
            const Icon = ICON[l.type] || Briefcase;
            const active = l.id === user?.company_id;
            return (
              <DropdownMenuItem key={l.id} onSelect={(e) => { e.preventDefault(); if (!active) switchTo(l); }} data-testid={`location-switcher-item-${l.id}`}
                className={`gap-3 rounded-lg px-3 py-2 text-sm cursor-pointer ${active ? "bg-primary/5" : ""}`}>
                <Icon className="h-4 w-4 text-slate-500 shrink-0" />
                <span className="flex-1 truncate">{l.name}{l.is_primary && <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">Primary</span>}</span>
                {busy === l.id ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : active ? <Check className="h-4 w-4 text-primary" /> : null}
              </DropdownMenuItem>
            );
          })}
          {list.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No match.</div>}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => nav("/corporate/locations")} data-testid="location-switcher-manage" className="rounded-lg px-3 py-2 text-xs text-slate-500 cursor-pointer">Manage locations…</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
